import {AxiosCall} from '../axios';
import {serializeQueryParams} from './PaymentHistoryService';
import {IAppointment} from './service-mappers/AppointmentMapper';
import {ServiceSuccessResponseType} from './service-mappers/BaseMapper';
import {ArrayResponse} from './service-mappers/CommonServiceMapper';

export const getAllAppointments = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  date: Date | string | null,
  filters?: any
): Promise<any> => {
  let url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointments`;
  const queryParams: string[] = [];
  if (date) {
    queryParams.push(`date=${date}`);
  }
  if (filters) {
    if (filters?.hour !== undefined && filters?.hour !== null && filters?.hour !== '') {
      queryParams.push(`hour=${encodeURIComponent(String(filters.hour))}`);
    }
    if (filters?.search_text) {
      queryParams.push(
        `search_text=${encodeURIComponent(String(filters.search_text).trim())}`
      );
    }
    if (filters?.status) {
      queryParams.push(`status=${encodeURIComponent(String(filters.status))}`);
    }
    if (filters?.type) {
      queryParams.push(`type=${encodeURIComponent(String(filters.type))}`);
    }
    if (filters?.statement_balance) {
      queryParams.push(
        `statement_balance=${encodeURIComponent(String(filters.statement_balance))}`
      );
    }
    if (
      filters?.eligibility_status &&
      (Array.isArray(filters.eligibility_status)
        ? filters.eligibility_status.length
        : true)
    ) {
      const eligibility = Array.isArray(filters.eligibility_status)
        ? filters.eligibility_status.join(',')
        : filters.eligibility_status;
      if (eligibility) {
        queryParams.push(
          `eligibility_status=${encodeURIComponent(String(eligibility))}`
        );
      }
    }
    if (filters?.patient_forms) {
      queryParams.push(
        `patient_forms=${encodeURIComponent(String(filters.patient_forms))}`
      );
    }
    if (filters?.verification_status) {
      queryParams.push(
        `verification_status=${encodeURIComponent(
          String(filters.verification_status)
        )}`
      );
    }
    if (
      filters?.is_manually_verified === true ||
      filters?.is_manually_verified === false ||
      filters?.is_manually_verified === 'true' ||
      filters?.is_manually_verified === 'false'
    ) {
      queryParams.push(`is_manually_verified=${filters.is_manually_verified}`);
    }
    if (filters?.vaccine_check === true || filters?.vaccine_check === 'true') {
      queryParams.push('vaccine_check=true');
    }
    if (filters?.review_type) {
      queryParams.push(
        `review_type=${encodeURIComponent(String(filters.review_type))}`
      );
    }
    if (filters?.consent_form_status) {
      queryParams.push(
        `consent_form_status=${encodeURIComponent(
          String(filters.consent_form_status)
        )}`
      );
    }
    if (filters?.rmd_only) {
      queryParams.push('rmd_only=true');
    }
    if (filters?.imported_only) {
      queryParams.push('imported_only=true');
    }
    if (filters?.posted_to_emr === false || filters?.posted_to_emr === 'false') {
      queryParams.push('posted_to_emr=false');
    } else if (filters?.posted_to_emr === true || filters?.posted_to_emr === 'true') {
      queryParams.push('posted_to_emr=true');
    }
    if (filters?.emr_sync_pending === true || filters?.emr_sync_pending === 'true') {
      queryParams.push('emr_sync_pending=true');
    } else if (
      filters?.emr_sync_pending === false ||
      filters?.emr_sync_pending === 'false'
    ) {
      queryParams.push('emr_sync_pending=false');
    }
    if (
      filters?.patient_posted_to_emr === true ||
      filters?.patient_posted_to_emr === 'true'
    ) {
      queryParams.push('patient_posted_to_emr=true');
    }
    if (
      filters?.patient_updated_in_emr === true ||
      filters?.patient_updated_in_emr === 'true'
    ) {
      queryParams.push('patient_updated_in_emr=true');
    }
    if (
      filters?.guarantor_posted_to_emr === true ||
      filters?.guarantor_posted_to_emr === 'true'
    ) {
      queryParams.push('guarantor_posted_to_emr=true');
    }
    if (
      filters?.guarantor_updated_in_emr === true ||
      filters?.guarantor_updated_in_emr === 'true'
    ) {
      queryParams.push('guarantor_updated_in_emr=true');
    }
    if (filters?.limit != null) {
      queryParams.push(`limit=${filters.limit}`);
    }
    if (filters?.offset != null) {
      queryParams.push(`offset=${filters.offset}`);
    }
    if (
      filters?.pending_guarantor_info_submission === true ||
      filters?.pending_guarantor_info_submission === 'true'
    ) {
      queryParams.push('pending_guarantor_info_submission=true');
    }
    if (filters?.source === 'voice-assistant') {
      queryParams.push('source=voice-assistant');
    }
  }
  if (queryParams.length > 0) {
    url = `${url}?${queryParams.join('&')}`;
  }

  return AxiosCall.get(url);
};

export const getVoiceCallSessions = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  options?: {
    callStatusFilter?: string;
    appointmentStatusFilter?: string;
    page?: number;
    pageSize?: number;
    searchText?: string;
    startDate?: string;
    endDate?: string;
  }
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/voice-call-sessions${
      queryParams ? `?${queryParams}` : ''
    }`
  );
};

export const getVoiceCallSessionDetails = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  session_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/voice-call-sessions/${session_id}`
  );
};

export const getVoiceCallSessionTranscriptions = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  session_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/voice-call-sessions/${session_id}/transcriptions`
  );
};

export const resolveVoiceCallSession = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  session_id: number | string,
  comment: string
): Promise<any> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/voice-call-sessions/${session_id}/mark-resolved`,
    {comment}
  );
};

export const updateAppointment = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  id: number | string,
  payload: any
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/${id}`;
  return AxiosCall.put(url, payload);
};

export const markAppointmentPostedToEmr = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  appointment_id: number | string,
  payload: {encounter_number?: string}
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/${appointment_id}/post-to-emr`,
    payload
  );
};

export const sendReminders = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  payload: any
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointments/send`;
  return AxiosCall.post(url, payload);
};

export const getAllPatientAppointments = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  patient_id: number | null,
  options?: {appointment_id?: number | string | null}
): Promise<any> => {
  let url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/patient/${patient_id}/appointments`;
  if (options?.appointment_id != null && options.appointment_id !== '') {
    url += `?${serializeQueryParams({appointment_id: options.appointment_id})}`;
  }
  return AxiosCall.get(url);
};

export const getAppointmentsLastImport = async (
  clinic_group_id: number | string,
  clinic_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/appointment/import/last-import-date?clinic_id=${clinic_id}`;

  return AxiosCall.get(url);
};

export const getAppointmentTypes = async (
  clinic_group_id: number | string,
  clinic_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment-types`;
  return AxiosCall.get(url);
};

export const getAppointmentProviders = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  appointment_type_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment-type/${appointment_type_id}/providers`;
  return AxiosCall.get(url);
};

export const getAppointmentById = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  appointment_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/${appointment_id}`
  );
};

export const getConsentFormReview = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  appointment_id: number | string,
  payload?: {
    consent_review_notes: string;
  }
): Promise<any> => {
  const url = `clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/${appointment_id}/review-consent`;
  return AxiosCall.put(url, payload);
};

// TODO: CHANGE ITS NAME
export const fetchAllAppointments = async ({
  clinic_group_id,
  params
}: {
  clinic_group_id: number;
  params?: {
    clinic_id?: number;
    patient_id?: number;
  };
}): Promise<ServiceSuccessResponseType<ArrayResponse<IAppointment>>> => {
  const queryParams = serializeQueryParams(params);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/appointment/search/all${
      queryParams ? '?' + queryParams : ''
    }`
  );
};

export const createBulkPriorAuth = ({
  clinic_group_id,
  clinic_id,
  payload
}: {
  clinic_group_id: number;
  clinic_id: number;
  payload:
    | {
        appointment_start_date: string;
        appointment_end_date: string;
        visit_type: string[];
      }
    | {appointment_id: number[]; visit_type: string[]};
}): Promise<ServiceSuccessResponseType<never>> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/prior-authorization/bulk-create`,
    payload
  );
};

export const getAppointmentVisitTypes = ({
  clinic_group_id,
  clinic_id,
  params
}: {
  clinic_group_id: number;
  clinic_id: number;
  params?: {appointment_start_date: string; appointment_end_date: string};
}): Promise<ServiceSuccessResponseType<ArrayResponse<{type: string}>>> => {
  const queryParams = serializeQueryParams(params);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/visit-types/all?${queryParams}`
  );
};

export const getAllClinicVaccines = ({
  clinic_group_id,
  clinic_id,
  appointment_type
}: {
  clinic_group_id: number;
  clinic_id: number | string;
  appointment_type?: string;
}) => {
  const queryParams = serializeQueryParams({appointment_type});
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/vaccines${
      queryParams ? '?' + queryParams : ''
    }`
  );
};

export const getAppointmentSlots = ({
  clinic_group_id,
  clinic_id,
  params
}: {
  clinic_group_id: number;
  clinic_id: number | string;
  params: Partial<{
    date: string;
    vaccine_id?: number;
    patient_id: number;
    provider_id?: number;
    appointment_type_id: number;
  }>;
}) => {
  const queryParams = serializeQueryParams(params);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/slots/available?${queryParams}`
  );
};

export const createAppointment = ({
  clinic_group_id,
  clinic_id,
  payload
}: {
  clinic_group_id: number;
  clinic_id: number;
  payload: {
    vaccine_id?: number;
    patient_id: number;
    appointment_date: string;
    duration: number;
    provider_id?: number;
    appointment_type_id: number;
  };
}) => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/new`,
    payload
  );
};
